import { Directive, ElementRef, HostListener, OnInit } from '@angular/core';

/** Fallback when `--tt-filter-search-duration` is missing. Matches `--tt-duration-overlay`. */
const CLOSE_ANIMATION_MS = 180;

@Directive({
  selector: 'details[appDetailsCloseOnOutsideClick]',
})
export class DetailsCloseOnOutsideClickDirective implements OnInit {
  private closing = false;

  constructor(private readonly host: ElementRef<HTMLElement>) {}

  ngOnInit(): void {
    const details = this.host.nativeElement;
    if (!details.classList.contains('tt-filter-search-disclosure')) return;
    // Opened field uses global `.input` (white + #9CA3AF in light) without editing feature templates.
    details.querySelector('input, textarea')?.classList.add('input');
  }

  @HostListener('toggle', ['$event'])
  onToggle(event: Event): void {
    const details = event.target as HTMLDetailsElement;
    if (details !== this.host.nativeElement) return;
    if (details.open) {
      this.closing = false;
      details.classList.remove('tt-filter-search-disclosure--closing');
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const details = this.host.nativeElement as HTMLDetailsElement;
    if (!details.open) return;
    if (this.host.nativeElement.contains(event.target as Node)) return;
    this.closeAnimated();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeAnimated();
  }

  private closeAnimated(): void {
    const details = this.host.nativeElement as HTMLDetailsElement;
    if (!details.open || this.closing) return;

    this.closing = true;
    details.classList.add('tt-filter-search-disclosure--closing');

    const duration = this.closeDurationMs();

    window.setTimeout(() => {
      details.classList.remove('tt-filter-search-disclosure--closing');
      details.open = false;
      this.closing = false;
    }, duration);
  }

  private closeDurationMs(): number {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 0;
    const raw = getComputedStyle(this.host.nativeElement)
      .getPropertyValue('--tt-filter-search-duration')
      .trim();
    if (raw.endsWith('ms')) {
      const ms = Number.parseFloat(raw);
      if (Number.isFinite(ms)) return ms;
    }
    if (raw.endsWith('s')) {
      const seconds = Number.parseFloat(raw);
      if (Number.isFinite(seconds)) return seconds * 1000;
    }
    return CLOSE_ANIMATION_MS;
  }
}
